// TaskList.jsx — filterable list of a client's tasks with quick complete-toggle.
import React, { useState } from "react";
import { api } from "../api";
import { fmtDate, SC, PC } from "../utils";
import { useToast } from "../ui";
import { Ico, StatusPill } from "./primitives";

export default function TaskList({ tasks, onRefresh }) {
  const toast = useToast();
  const [filter, setFilter] = useState("all");
  const shown = tasks.filter((t) => filter === "all" || t.status === filter);

  const toggle = async (t) => {
    try {
      await api.patch(`/crm/tasks/${t._id}`, {
        status: t.status === "completed" ? "pending" : "completed",
      });
      onRefresh();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "in_progress", "completed", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all capitalize ${
              filter === s ? "text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
            style={filter === s ? { background: "var(--brand)" } : {}}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
        <span className="ml-auto text-[10px] text-slate-400 self-center">
          {shown.length} task{shown.length !== 1 ? "s" : ""}
        </span>
      </div>

      {!shown.length ? (
        <p className="text-sm text-slate-400 text-center py-8">No tasks match this filter</p>
      ) : (
        shown.map((t) => (
          <div
            key={t._id}
            className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-all group"
          >
            <button
              onClick={() => toggle(t)}
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                t.status === "completed"
                  ? "bg-emerald-500 border-emerald-500"
                  : "border-slate-300 hover:border-[rgb(var(--brand-rgb))]"
              }`}
            >
              {t.status === "completed" && <Ico d="M5 13l4 4L19 7" sz={9} col="white" />}
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className={`text-sm font-bold leading-snug ${t.status === "completed" ? "line-through text-slate-400" : "text-slate-800"}`}>
                  {t.taskTitle}
                </p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${PC[t.priority] || "bg-slate-100 text-slate-500"}`}>
                    {t.priority}
                  </span>
                  <StatusPill label={t.status} colorClass={SC[t.status] || "bg-slate-100 text-slate-500"} />
                </div>
              </div>

              {t.description && (
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed line-clamp-2">{t.description}</p>
              )}

              <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                {t.category && (
                  <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-lg font-semibold capitalize">
                    {t.category}
                  </span>
                )}
                {t.dueDate && (
                  <span className="flex items-center gap-1">
                    <Ico d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" sz={10} col="#94a3b8" />
                    Due {fmtDate(t.dueDate)}
                  </span>
                )}
                {t.reminderDate && (
                  <span className="flex items-center gap-1">
                    <Ico d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1" sz={10} col="#94a3b8" />
                    Reminder {fmtDate(t.reminderDate)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
